/**
 * The SVG dimension bars + end ticks for the current selection. Read-only
 * graphics: the group swallows no pointer events so the interactive handles
 * underneath keep working.
 *
 * `MeasureBarLines` is the drawing itself, over already-placed bars, so the
 * assembler (which measures rooms, not walls) draws its bars the same way.
 * The bars come from core's `placedBars`, as their chips do, so the bars stay
 * reachable while the view is zoomed in, each family shifting as one unit.
 */

import { EditorMutableState, type PlacedBar, placedBars } from '@kaiizen/kai-floor-plan-editor-core';
import { DIM_LINE_EMPHASIZED_STYLE, DIM_LINE_STYLE, DIM_TICK_EMPHASIZED_STYLE, DIM_TICK_STYLE } from './planCss';

export function MeasureBarLines({ placed }: { placed: readonly PlacedBar[] }): React.JSX.Element {
  const t = 5;
  return (
    <g pointerEvents='none'>
      {placed.map(({ bar, g }) => {
        const lineStyle = bar.emphasized ? DIM_LINE_EMPHASIZED_STYLE : DIM_LINE_STYLE;
        const tickStyle = bar.emphasized ? DIM_TICK_EMPHASIZED_STYLE : DIM_TICK_STYLE;
        const len = Math.hypot(g.bx - g.ax, g.by - g.ay);
        let ticks: React.JSX.Element | null = null;
        if (len > 0.5) {
          const dx = (g.bx - g.ax) / len, dy = (g.by - g.ay) / len;
          const px = -dy * t, py = dx * t;
          ticks = (
            <>
              <line css={tickStyle} x1={g.ax + px} y1={g.ay + py} x2={g.ax - px} y2={g.ay - py} />
              <line css={tickStyle} x1={g.bx + px} y1={g.by + py} x2={g.bx - px} y2={g.by - py} />
            </>
          );
        }
        return (
          <g key={bar.key} data-bar={bar.type}>
            <line css={lineStyle} x1={g.ax} y1={g.ay} x2={g.bx} y2={g.by} />
            {ticks}
          </g>
        );
      })}
    </g>
  );
}

export function DimBars({ st, stageW, stageH }: { st: EditorMutableState; stageW: number; stageH: number }): React.JSX.Element {
  return <MeasureBarLines placed={placedBars(st, stageW, stageH)} />;
}
